import React from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { Helmet } from 'react-helmet';
import { RouteComponentProps, navigate } from '@reach/router';
import { ITasks, RootState } from 'store/store';
import { Tasks } from './styles';
import messages from './Messages';
import TasksListContainer from './TasksListContainer';
import LogoLoading from 'components/LogoLoading';
import { lightGrayBackground } from 'utils/style/colors';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { ICompany, IProjectStore } from 'store/companies/companiesTypes';
import { languageDirection } from '../../../i18n';
import UserMixpanel from '../../../utils/mixpanel/MixpanelClass';
import { isANumber } from '../../../utils/number';

interface IStateToProps {
  thisUser: IThisUserState;
  company: ICompany;
  project?: IProjectStore;
  tasks: ITasks;
}

interface IDispatchToProps {}

interface IOwnProps {
  companyId?: string;
  projectId?: string;
  taskId?: number;
  children?: React.ReactNode;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

const TasksContainer: React.FC<IComponentProps> = (props) => {
  const handleClickOnTask = (
    taskId?: number | 'new',
    templateId?: number,
    boardField?: any,
    selectedBoardFieldTitle?: string
  ) => {
    const companyId = Number(props.companyId);
    const projectId = Number(props.projectId);
    const mx = UserMixpanel.getInstance();
    mx.visitedTasksCountHandler();
    if (isANumber(taskId)) {
      mx.handleSetVisitedTaskCountByTemplates(
        props.tasks.data[taskId!]?.taskName
      );
    }
    let url = `/t/${companyId}/task/editor`;
    if (projectId) {
      url = `/t/${companyId}/project/${projectId}/task/editor`;
    }
    if (taskId) {
      url = `${url}/${taskId}/info`;
    }

    if (
      typeof templateId === 'number' &&
      typeof boardField === 'object' &&
      typeof selectedBoardFieldTitle === 'string' &&
      (boardField.title || boardField.name)
    ) {
      url = `${url}?tempId=${templateId}&field=${boardField.title || boardField.name}&fieldVal=${selectedBoardFieldTitle}`;
    }

    navigate(url);
  };

  if (!props.project && props.projectId) {
    return <LogoLoading background={lightGrayBackground} />;
  }

  const { company, project, companyId, projectId } = props;
  let taskId: number | boolean = false;
  if (window.location.pathname.indexOf('editor/') > 0) {
    const pathEnd = window.location.pathname.substring(
      window.location.pathname.indexOf('editor/') + 'editor/'.length
    );
    taskId = Number(pathEnd.substring(0, pathEnd.indexOf('/'))) || true;
  }

  return (
    <div className="h-full relative">
      <Helmet>
        <title>
          {messages.tasksOf(
            project ? project?.name || '' : company?.name || ''
          )}
        </title>
        <meta
          name="description"
          content={messages.tasksOf(
            project ? project?.name || '' : company?.name || ''
          )}
        />
      </Helmet>
      {
        <Tasks>
          <TasksListContainer
            taskSelected={taskId}
            companyId={Number(companyId)}
            projectId={projectId ? Number(projectId) : undefined}
            onOpenTaskEditor={handleClickOnTask}
          />
          <div
            className={`absolute ${languageDirection() === 'rtl' ? 'right-0' : 'left-0'} shadow-md rounded-xl h-full ${taskId ? 'w-full' : 'w-0'}`}
          >
            {props.children}
          </div>
        </Tasks>
      }
    </div>
  );
};

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const companyId = Number(props.companyId);
  const projectId = Number(props.projectId);
  const thisUser = state.thisUser;
  const company = state.companies.data[companyId];
  const project = company?.projects[projectId];
  const tasks = state.tasks;

  return { thisUser, project, company, tasks };
};

export default connect(mapStateToProps)(TasksContainer);
